import {BasicDataTable, TypedColumnSettings} from "@/shared/ui/basicDataTable"
import {BaseTree} from "../ui/baseTree";
import {AssignmentTree} from "./assignmentTree";
import {htmlEscape} from "../utils/string";
import {
	AssignmentPanel,
	AssignmentPanelComponent,
	AssignmentPanelConfiguration,
	BaseAssignmentTable,
	UserSelectTable
} from "./assignmentPanel";
import {EMPTY_CALLBACK} from "@/shared/utils/misc";
import {AssignedGroup, AssignedUser, Car, Group, Identifiable, Task, TreeNode, User} from "@/shared/timr";

export abstract class BasicSameTypeAssignmentPanel<T extends Identifiable,
	TComponent extends AssignmentPanelComponent = AssignmentPanelComponent> extends AssignmentPanel<T> {

	protected assignedComponent: TComponent;
	protected selectComponent: TComponent;
	protected overviewComponent: TComponent;

	protected constructor(config: AssignmentPanelConfiguration<T>) {
		super(config);

		this.assignedComponent = this.createAssignedComponent(this.config.panelElement.find(".assigned-element"));
		this.selectComponent = this.createSelectComponent(this.config.panelElement.find(".select-element"));
		this.overviewComponent = this.createAssignmentOverviewTable(this.config.panelElement.find(".overview-element"));

		$(this.selectComponent).on(BaseAssignmentTable.EVENT_ENTRY_REMOVED, (event, element: T) => {
			this.assign(element);
		});

		$(this.assignedComponent).on(BaseAssignmentTable.EVENT_ENTRY_REMOVED, (event, element: T) => {
			this.unassign(element);
		});
	}

	public filterSelection(filter: string) {
		this.selectComponent.searchOfflineData(filter);
		this.assignedComponent.searchOfflineData(filter);
		this.overviewComponent.searchOfflineData(filter);
	}

	protected updateEditView() {
		this.assignedComponent.reload();
		this.selectComponent.reload();
	}

	protected updateOverview() {
		this.overviewComponent.reload();
	}

	public setScrollHeight(height: number) {
		this.assignedComponent.setScrollHeight(height);
		this.selectComponent.setScrollHeight(height);
		this.overviewComponent.setScrollHeight(height);

		this.assignedComponent.redrawCurrentPage();
		this.selectComponent.redrawCurrentPage();
		this.overviewComponent.redrawCurrentPage();
	}

	protected abstract createSelectComponent(element: JQuery): TComponent;

	protected abstract createAssignedComponent(element: JQuery): TComponent;

	protected abstract createAssignmentOverviewTable(element: JQuery): TComponent;

	protected getElementsForSelection(): PromiseLike<T[]> {
		const allAssigned = this.getAssigned();
		return this.config.selectSourceProvider().then((elements) => {
			return elements.filter(t1 => {
				return !allAssigned.some(t2 => this.equalityFunction(t1, t2));
			});
		});
	}
}

export class AssignmentPanelTreeWrapper<T extends Identifiable>
	implements Pick<AssignmentTree<T>, keyof AssignmentTree<T>>, // pick all public members
		AssignmentPanelComponent {

	constructor(private wrapped: AssignmentTree<T>, private onReload: (tree: AssignmentTree<T>) => void) {

	}

	public assign(t: T) {
		return this.wrapped.assign(t);
	}

	public unassign(t: T) {
		return this.wrapped.unassign(t);
	}

	public isAssigned(t: T): boolean {
		return this.wrapped.isAssigned(t);
	}

	public setAssigned(ts: (T | string)[]) {
		this.wrapped.setAssigned(ts);
	}

	public getBaseTree(): BaseTree {
		return this.wrapped.getBaseTree();
	}

	public redrawCurrentPage() {
		// Empty
	}

	public reload() {
		this.onReload(this.wrapped);
	}

	public searchOfflineData(filter: string) {
		this.wrapped.getBaseTree().queryTree(filter);
	}

	public setScrollHeight(height: number) {
		this.wrapped.getBaseTree().setMaxHeight(height + 5);
	}

	public addTask(node: (T & TreeNode), parentAccessor: (node: (T & TreeNode)) => ((T & TreeNode) | null)) {
		this.wrapped.addTask(node, parentAccessor);
	}

	public getAssigned(): string[] {
		return this.wrapped.getAssigned();
	}
}

export class CarAssignmentPanel extends BasicSameTypeAssignmentPanel<Car> {
	constructor(protected config: AssignmentPanelConfiguration<Car>) {
		super(config);
	}

	protected createAssignedComponent(element: JQuery): AssignmentPanelComponent {
		return new CarAssignedTable(() => this.getAssigned(), {
			element: element,
			orderable: false,
			allowExpand: false,
			clampNote: false
		});
	}

	protected createSelectComponent(element: JQuery): AssignmentPanelComponent {
		return new CarSelectTable(() => this.getElementsForSelection(), {
			element: this.config.panelElement.find("table.select-element"),
			orderable: false,
			allowExpand: false,
			clampNote: false
		});
	}

	protected createAssignmentOverviewTable(element: JQuery): BasicDataTable<Car> {
		return new CarAssignmentOverviewTable(() => this.getAssigned(), {
			element: element,
			orderable: false,
			allowExpand: false,
			clampNote: false
		});
	}
}

export class UserAssignmentPanel extends BasicSameTypeAssignmentPanel<AssignedUser> {
	constructor(config: AssignmentPanelConfiguration<AssignedUser>) {
		super(config);
	}

	protected createAssignedComponent(element: JQuery): AssignmentPanelComponent {
		return new UserAssignedTable(() => this.getAssigned(), {
			element: element,
			orderable: false,
			allowExpand: false,
			clampNote: false
		});
	}

	protected createSelectComponent(element: JQuery): AssignmentPanelComponent {
		return new UserSelectTable(() => this.getElementsForSelection(), {
			element: element,
			orderable: false,
			allowExpand: false,
			clampNote: false
		});
	}

	protected createAssignmentOverviewTable(element: JQuery): BasicDataTable<AssignedUser> {
		return new UserAssignmentOverViewTable(() => this.getAssigned(), {
			element: element,
			orderable: false,
			allowExpand: false,
			clampNote: false
		});
	}
}

export class GroupAssignmentPanel extends BasicSameTypeAssignmentPanel<AssignedGroup> {
	constructor(config: AssignmentPanelConfiguration<AssignedGroup>) {
		super(config);
	}

	protected createAssignedComponent(element: JQuery): AssignmentPanelComponent {
		return new GroupAssignedTable(() => this.getAssigned(), {
			element: element,
			orderable: false,
			allowExpand: false,
			clampNote: false
		});
	}

	protected createSelectComponent(element: JQuery): AssignmentPanelComponent {
		return new AssignmentPanelTreeWrapper<Group>(new AssignmentTree(new BaseTree({
			treeElement: element,
			loadRootNodesURL: "admin/loadAllGroups.json",
			loadSubNodesURL: "admin/loadSubGroups.json",
			filterNodesURL: "reports/filterGroups.json",
			// empty object to prevent a "Load Error" group if the list is empty
			getRequestData: () => ({}),
			isNodeSelectable: () => false
		}), (node) => {
			this.assign(node.data as AssignedGroup);
		}), (tree) => {
			tree.setAssigned(this.getAssigned());
		});
	}

	protected createAssignmentOverviewTable(element: JQuery): BasicDataTable<AssignedGroup> {
		return new GroupAssignmentOverviewTable(() => this.getAssigned(), {
			element: element,
			orderable: false,
			allowExpand: false,
			clampNote: false
		});
	}
}


/*
 *
 * Tables for the different assignment panels follow:
 *
 */

class CarAssignedTable extends BaseAssignmentTable<Car> {
	protected getColumnSettings(): TypedColumnSettings<Car>[] {
		return [
			{
				data: "name", orderable: true,
				render: BasicDataTable.renderText
			},
			{
				data: "plate", orderable: true,
				render: BasicDataTable.renderText
			},
			{
				className: "no-padding-top no-padding-bottom no-padding-left",
				orderable: false,
				render: (d: void, t: string, r: Car, m: DataTables.CellMetaSettings) => this.renderControlButton("changeAssignmentButton", messages["group.user.remove"], "fa-minus"),
				width: BasicDataTable.COLUMN_SIZE_QUICK_SWITCH_BUTTON
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "asc"]];
	}
}

class CarSelectTable extends BaseAssignmentTable<Car> {
	protected getColumnSettings(): TypedColumnSettings<Car>[] {
		return [
			{
				orderable: false,
				className: "no-padding-top no-padding-bottom no-padding-right",
				render: () => this.renderControlButton("changeAssignmentButton", messages["group.user.add"], "fa-plus"),
				width: BasicDataTable.COLUMN_SIZE_QUICK_SWITCH_BUTTON
			},
			{data: "name", orderable: true, render: BasicDataTable.renderText, searchable: true},
			{
				data: "plate", orderable: true,
				render: BasicDataTable.renderText
			},
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[1, "asc"]];
	}
}

class CarAssignmentOverviewTable extends BasicDataTable<Car> {
	protected getColumnSettings(): TypedColumnSettings<Car>[] {
		return [
			{
				data: "name", orderable: true,
				render: BasicDataTable.renderText
			},
			{
				data: "plate", orderable: true,
				render: BasicDataTable.renderText
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "asc"]];
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt`,
			paging: false,
			scrollY: "300px",
			scrollCollapse: true,
			searching: true
		};
	}
}

class UserAssignedTable extends BaseAssignmentTable<AssignedUser> {
	protected getColumnSettings(): TypedColumnSettings<AssignedUser>[] {
		return [
			{
				// use autoAssigned as column definition data to order the elements bundled by this flag (fullname is displayed)
				data: "autoAssigned",
				type: "Boolean",
				orderable: true,
				render: (d, t, r, m) => this.renderNameText(d, t, r, m)
			},
			{
				className: "no-padding-top no-padding-bottom no-padding-left",
				orderable: false,
				render: (d: void, t: string, r: AssignedUser, m: DataTables.CellMetaSettings) => this.renderUnAssignButton(d, t, r, m),
				width: BasicDataTable.COLUMN_SIZE_QUICK_SWITCH_BUTTON
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "asc"]];
	}

	protected renderNameText(data: boolean, type: string, row: AssignedUser, meta: DataTables.CellMetaSettings) {
		const classes = data === true ? ` class="text-grey"` : "";
		return `<span ${classes}>${htmlEscape(row.fullname)}</span>`;
	}

	protected renderUnAssignButton(data: void, type: string, row: AssignedUser, meta: DataTables.CellMetaSettings) {
		if (row.autoAssigned === true) {
			return "";
		} else {
			return this.renderControlButton("changeAssignmentButton", messages["group.user.remove"], "fa-minus");
		}
	}
}

class GroupAssignedTable extends BaseAssignmentTable<AssignedGroup> {
	protected getColumnSettings(): TypedColumnSettings<AssignedGroup>[] {
		return [
			{
				orderable: true,
				render: (d: void, t: string, r: AssignedGroup, m: DataTables.CellMetaSettings) => this.renderNameText(d, t, r, m)
			},
			{
				className: "no-padding-top no-padding-bottom no-padding-left",
				orderable: false,
				render: (d: void, t: string, r: AssignedGroup, m: DataTables.CellMetaSettings) => this.renderUnAssignButton(d, t, r, m),
				width: BasicDataTable.COLUMN_SIZE_QUICK_SWITCH_BUTTON
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "asc"]];
	}

	protected renderNameText(data: void, type: string, row: AssignedGroup, meta: DataTables.CellMetaSettings) {
		const classes = row.autoAssigned === true ? ` class="text-grey"` : "";

		const text = row.name;

		return `<span ${classes}>${htmlEscape(text)}</span>`;
	}

	protected renderUnAssignButton(data: void, type: string, row: AssignedGroup, meta: DataTables.CellMetaSettings) {
		if (row.autoAssigned === true) {
			return "";
		} else {
			return this.renderControlButton("changeAssignmentButton", messages["group.user.remove"], "fa-minus");
		}
	}
}

class GroupAssignmentOverviewTable extends BasicDataTable<AssignedGroup> {
	protected getColumnSettings(): TypedColumnSettings<AssignedGroup>[] {
		return [
			{
				orderable: true,
				render: (d: void, t: string, r: AssignedGroup, m: DataTables.CellMetaSettings) => this.renderNameText(d, t, r, m)
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "asc"]];
	}

	protected renderNameText(data: void, type: string, row: AssignedGroup, meta: DataTables.CellMetaSettings) {
		const classes = row.autoAssigned === true ? ` class="text-grey"` : "";

		const text = row.name;

		return `<span ${classes}>${htmlEscape(text)}</span>`;
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt`,
			paging: false,
			scrollY: "300px",
			scrollCollapse: true,
			searching: true
		};
	}
}

class UserAssignmentOverViewTable extends BasicDataTable<AssignedUser> {
	protected getColumnSettings(): TypedColumnSettings<AssignedUser>[] {
		return [
			{
				// use autoAssigned as column definition data to order the elements bundled by this flag (fullname is displayed)
				data: "autoAssigned",
				type: "Boolean",
				orderable: true,
				render: (d, t, r, m) => this.renderNameText(d, t, r, m)
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "asc"]];
	}

	protected renderNameText(data: boolean, type: string, row: User, meta: DataTables.CellMetaSettings) {
		const classes = data === true ? ` class="text-grey"` : "";
		return `<span ${classes}>${htmlEscape(row.fullname)}</span>`;
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt`,
			paging: false,
			scrollY: "300px",
			scrollCollapse: true,
			searching: true,
			rowCallback: (row, data, index) => {
				const user = data as AssignedUser;
				if (user.disabled) {
					$(row).addClass("archived");
				}
			}
		};
	}
}

export class TaskAssignmentPanel extends BasicSameTypeAssignmentPanel<Task, AssignmentPanelTreeWrapper<Task>> {
	private static MIN_QUERY_LENGTH = 3;

	private filterInputContainer: JQuery;

	private userId: number;

	private unassigned: Task[];


	constructor(config: AssignmentPanelConfiguration<Task>, private userIdProvider: () => number | null) {
		super(config);

		this.filterInputContainer = this.config.panelElement.find("input.filter-input").closest("div.input-group");

		this.selectComponent.getBaseTree().config.treeElement.on("fancytreeexpand", ((event, ctx: Fancytree.EventData) => {
			const node = this.assignedComponent.getBaseTree().getNodeByKey(ctx.node.key);
			if (node) {
				node.setExpanded(true);
			}
		}));

		this.overviewComponent.getBaseTree().config.treeElement.on("fancytreeexpand", ((event, ctx: Fancytree.EventData) => {
			const node = this.assignedComponent.getBaseTree().getNodeByKey(ctx.node.key);
			if (node) {
				node.setExpanded(true);
			}
		}));

		this.unassigned = [];
	}

	public setEditMode(editMode: boolean) {
		super.setEditMode(editMode);
		this.filterInputContainer.toggle(editMode);
	}

	protected createAssignedComponent(element: JQuery): AssignmentPanelTreeWrapper<Task> {
		const assignedTree = new AssignmentTree<Task>(new BaseTree({
			treeElement: element,
			loadRootNodesURL: "admin/loadUserRootTaskAssignments.json",
			loadSubNodesURL: "admin/loadUserSubTaskAssignments.json",
			filterNodesURL: "",
			deferLoading: true,
			getRequestData: () => ({
				hideInactive: false,
				userId: this.userId
			}),
			isNodeSelectable: () => false,
			onRenderNode: (event, data) => {
				const node = data.node;
				const nodeSpan = $(node.span);
				const task = node.data as Task;
				if (task?.disabled) {
					nodeSpan.addClass("tree-node-disabled");
				}
			}
		}), (node) => {
			this.assign(node.data as Task);
		}, (node) => {
			this.unassign(node.data as Task);
		}, true);

		return new AssignmentPanelTreeWrapper(assignedTree, EMPTY_CALLBACK);
	}

	protected createAssignmentOverviewTable(element: JQuery): AssignmentPanelTreeWrapper<Task> {
		const overviewTree = new AssignmentTree<Task>(new BaseTree({
			treeElement: element,
			loadRootNodesURL: "admin/loadUserRootTaskAssignments.json",
			loadSubNodesURL: "admin/loadUserSubTaskAssignments.json",
			filterNodesURL: "",
			deferLoading: true,
			getRequestData: () => ({
				hideInactive: false,
				userId: this.userId
			}),
			isNodeSelectable: () => false,
			onRenderNode: (event, data) => {
				const node = data.node;
				const nodeSpan = $(node.span);

				const nodeData = node.data as Task;
				nodeSpan.toggleClass("text-grey", !nodeData.assignedToCurrentUser);
				if (nodeData?.disabled) {
					nodeSpan.addClass("tree-node-disabled");
				}
			}
		}), false, false, false);

		return new AssignmentPanelTreeWrapper(overviewTree, EMPTY_CALLBACK);
	}

	protected createSelectComponent(element: JQuery): AssignmentPanelTreeWrapper<Task> {
		const selectTree = new AssignmentTree<Task>(new BaseTree({
			treeElement: element,
			loadRootNodesURL: "tasks/loadAllTasks.json",
			loadSubNodesURL: "tasks/taskTreeLoad.json",
			filterNodesURL: "tasks/filterTasks.json",
			getRequestData: () => ({
				hideInactive: true
			}),
			isNodeSelectable: () => false,
			minQueryLength: TaskAssignmentPanel.MIN_QUERY_LENGTH,
		}), (node) => {
			this.assign(node.data as Task);
		});

		return new AssignmentPanelTreeWrapper(selectTree, EMPTY_CALLBACK);
	}

	public unassign(task: Task) {
		if (!this.unassigned.some(t => t.id === task.id)) {
			this.unassigned.push(task);
		}

		super.unassign(task);

		const node = this.overviewComponent.getBaseTree().getNodeByKey(task.id + "");

		this.removeParentTasksNotAssigned(task.parentTask);

		if (node && node.getParent()) {
			node.remove();
		}
	}

	private removeParentTasksNotAssigned(startTask: Task) {
		let task = startTask;
		while (task != null && !task.assignedToCurrentUser) {
			const taskToRemove = task;

			task = task.parentTask;

			this.overviewComponent.getBaseTree().getNodeByKey(taskToRemove.id + "")?.remove();
		}
	}

	public getUnassigned(): Task[] {
		return this.unassigned;
	}

	public assign(task: Task) {
		this.unassigned = this.unassigned.filter(t => t.id !== task.id);

		super.assign(task);

		// purge tree of dummy nodes
		this.assignedComponent.getBaseTree().removeDummyNodes();

		// add assigned to tree
		const taskNode = task as Task & TreeNode;
		taskNode.title = task.name; // Needed because fancytree deletes the title field
		taskNode.key = String(task.id);
		taskNode.assignedToCurrentUser = true;
		this.assignedComponent.addTask(taskNode, node => node.parentTask as Task & TreeNode);
	}

	public reloadAssigned() {
		this.userId = this.userIdProvider();

		this.assignedComponent.setAssigned([]);
		this.assignedComponent.getBaseTree().setDisableLoading(!this.userId);
		this.overviewComponent.getBaseTree().setDisableLoading(!this.userId);

		super.reloadAssigned();
		this.unassigned = [];
	}

	protected updateEditView() {
		const assigned: string[] = [];
		assigned.push(...this.assignedComponent.getAssigned());
		assigned.push(...this.getAssigned().map(el => el.id + ""));

		const selected = assigned.filter(id => !this.unassigned.some(el => (el.id + "") === id));

		this.selectComponent.setAssigned(selected);
	}


	protected updateOverview() {
		this.getUnassigned().forEach(task => {
			const node = this.overviewComponent.getBaseTree().getNodeByKey(task.id + "");
			if (node != null) {
				node.remove();
			}
		});

		const assigned = this.getAssigned();
		if (assigned.length > 0) {
			this.overviewComponent.getBaseTree().removeDummyNodes();
		}

		assigned.forEach(task => {
			const taskNode = task as Task & TreeNode;
			taskNode.title = task.name; // Needed because fancytree deletes the title field
			taskNode.key = String(task.id);
			taskNode.assignedToCurrentUser = true;

			this.overviewComponent.addTask(taskNode, node => node.parentTask as Task & TreeNode);
		});
	}

	public filterSelection(filter: string) {
		this.selectComponent.getBaseTree().queryTree(filter);
	}

	public setScrollHeight(height: number) {
		this.selectComponent.getBaseTree().setMaxHeight(height + 5);
		this.assignedComponent.getBaseTree().setMaxHeight(height + 5);
		this.overviewComponent.getBaseTree().setMaxHeight(height + 5);
	}
}
